import React from "react";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, BookOpen } from "lucide-react";
import type {
  Candidate,
  QuestionGroup,
  College,
  JobPosition,
} from "@/types/database";

interface AssessmentHeaderProps {
  candidate: Candidate;
  timeRemaining: number | null;
  currentGroup?: QuestionGroup | null;
  currentGroupIndex: number;
  totalGroups: number;
  college?: College | null;
  jobPosition?: JobPosition | null;
  hideTimer?: boolean; // New prop to hide the timer
}

const formatTime = (seconds: number | null): string => {
  if (seconds === null) return "Loading...";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  }
  return `${String(minutes).padStart(2, "0")}:${String(
    remainingSeconds
  ).padStart(2, "0")}`;
};

export const AssessmentHeader = React.memo(
  ({
    candidate,
    timeRemaining,
    currentGroup,
    currentGroupIndex,
    totalGroups,
    college,
    jobPosition,
    hideTimer = false, // Default to false
  }: AssessmentHeaderProps) => {
    return (
      <Card className="w-full">
        <CardHeader className="flex flex-col p-6">
          {/* Top Row: Assessment Title, Section, and Time */}
          <div className="flex items-center justify-between w-full mb-4">
            <div className="flex items-center gap-3">
              <BookOpen className="h-6 w-6 text-blue-600" />
              <CardTitle className="text-xl font-bold text-gray-800">
                {currentGroup?.name || "Assessment"}
              </CardTitle>
              <span className="text-sm text-gray-600 bg-blue-100 px-3 py-1 rounded-full font-medium">
                Section {currentGroupIndex + 1} of {totalGroups}
              </span>
            </div>
            {!hideTimer && ( // Conditionally render the timer
              <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-md">
                <Clock className="h-5 w-5" />
                <span className="text-lg font-semibold">
                  {formatTime(timeRemaining)}
                </span>
              </div>
            )}
          </div>

          {/* Bottom Row: Candidate Info and Group Description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            <div className="space-y-1 text-gray-700">
              <p className="text-base font-semibold">
                Welcome, {candidate.full_name}
              </p>
              {jobPosition?.role_name && (
                <p className="text-sm">
                  <span className="font-medium">Applying for:</span>{" "}
                  {jobPosition.role_name}
                </p>
              )}
              {college?.name && (
                <p className="text-sm">
                  <span className="font-medium">College:</span> {college.name}
                </p>
              )}
              {candidate.email && (
                <p className="text-sm">
                  <span className="font-medium">Email:</span> {candidate.email}
                </p>
              )}
            </div>
            {currentGroup?.description && (
              <div className="space-y-1 text-gray-700">
                <p className="text-base font-semibold">Section Description:</p>
                <p className="text-sm max-w-full leading-relaxed">
                  {currentGroup.description}
                </p>
              </div>
            )}
          </div>
        </CardHeader>
      </Card>
    );
  }
);

AssessmentHeader.displayName = "AssessmentHeader";
